import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';

import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { getImageAttrs } from '~/modules/images';
import {
    NEXT_ARTICLE_OPENING_API_ROUTE,
    type loader as nextOpeningLoader,
} from '~/routes/remix-api.article.next-opening';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import {
    NEXT_LISTING_START_SCALE,
    useNextListingTurn,
} from '~/ui-rocco/pages/detail-shared/use-next-listing-turn';
import {
    NextListingInvitation,
    nextInvitationOpacity,
} from '~/ui-rocco/pages/detail-shared/NextListingInvitation';
import { ARTICLE_GROUND } from '../util/article-ground';
import { ARTICLE_HEADLINE_TYPE } from '../util/article-display-type';
import { REVAMP_HERO_SIZES } from '../util/revamp-image-sizes';

/**
 * The headline exactly as the masthead sets it over the banner: the header's
 * own offsets and measure, in white on the photograph. Any drift here is a
 * jump the reader sees at the turn.
 */
const HEADLINE = cn(
    'm-0 w-full text-left max-md:mt-4 md:mt-[3.75rem] md:-ml-[0.5rem] md:max-w-[88rem]',
    ARTICLE_HEADLINE_TYPE,
    'text-white',
);

/** Where the masthead puts the headline: the page gutters, 24.225rem down. */
/** Where the masthead puts its headline, over the banner. */
const HEADLINE_PLACE = cn(
    'pointer-events-none absolute inset-x-0 top-0 z-10',
    'px-4.5 md:px-[3rem]',
    'max-md:pt-6 md:pt-[24.225rem]',
);

/** The masthead's own box for the full-bleed banner. */
const HERO_BOX =
    'w-full max-md:aspect-[1920/940] md:aspect-[1200/868] md:max-h-[86.8125rem]';

/** The masthead's grade: a flat tint, and a scrim carrying the white type. */
const TINT = 'pointer-events-none absolute inset-0 bg-[#131313]/[0.22]';
const BOTTOM_SCRIM =
    'pointer-events-none absolute inset-x-0 bottom-0 h-[36.875rem] bg-gradient-to-t from-[#131313]/[0.89] from-[6.207%] to-transparent to-[70.169%]';

/** The next article's own headline arrives from here, as the banner does. */
const TITLE_FADE_FROM = 0.55;

const setOpacity = (node: HTMLElement | null, value: number) => {
    if (node) node.style.opacity = String(value);
};

/**
 * The foot of the article page: the next read, growing as the reader
 * arrives at it, and then becoming that page.
 *
 * What grows is the next page's own opening — its banner in the masthead's
 * rendition through the masthead's own image attributes, with its headline
 * where the masthead puts it — laid out at full size and scaled down from
 * there. So by the time the banner is full size, the reader is looking at
 * the top of the next page, and the turn is a swap of one document for an
 * identical one.
 *
 * The turn itself — the warm on approach, the readiness gates, the settled
 * commit at the scroll clamp and the landing — is `useNextListingTurn`,
 * shared with the product and project turns. This section owns what the
 * reader sees.
 *
 * The next read is chosen by the page — the first other article its
 * reading rails offer — so the turn continues the same thread the rails
 * do, and the page can stand its own closing padding down while the
 * section is the document's end. A premium or flexi next article opens on
 * its own masthead; the landing then falls back to the page top, which its
 * anchorless opening reads as.
 */
export const NextArticleTransition = ({
    next,
}: {
    /** The read the foot grows into: a rail tile with a link and a picture. */
    next: ArticleTileViewModel;
}) => {
    const { log } = useTracker();

    const titleRef = useRef<HTMLParagraphElement>(null);
    const labelRef = useRef<HTMLDivElement>(null);

    // The next article's opening as its page paints it. Loaded on arrival,
    // so the banner (and the browser's cache of its rendition) is in place
    // long before the reader reaches it.
    const opening = useFetcher<typeof nextOpeningLoader>();
    const openingLoad = opening.load;
    const nextLink = next.link;
    useEffect(() => {
        if (!nextLink) return;
        const params = new URLSearchParams({ link: nextLink });
        openingLoad(`${NEXT_ARTICLE_OPENING_API_ROUTE}?${params}`);
    }, [openingLoad, nextLink]);

    // The masthead's own rendition once it has answered for this article;
    // until then the listing's crop stands in.
    const answered =
        opening.data && opening.data.link === nextLink ? opening.data : null;
    const imageSrc = answered?.imageSrc ?? next.image?.src;
    const title = answered?.title ?? next.title;

    const { sectionRef, frameRef } = useNextListingTurn({
        link: nextLink,
        answered: Boolean(answered),
        active: Boolean(imageSrc),
        anchorSelector: '[data-article-hero]',
        onCommit: () =>
            log('NextArticleTurn', {
                url: new URL(window.location.href),
                data: { NextLink: nextLink ?? '', Trigger: 'auto' },
            }),
        // The next article's own headline arrives on the banner as it grows,
        // as the invitation above goes.
        onProgress: (progress) => {
            setOpacity(labelRef.current, nextInvitationOpacity(progress));
            setOpacity(
                titleRef.current,
                Math.min(
                    Math.max(
                        (progress - TITLE_FADE_FROM) / (1 - TITLE_FADE_FROM),
                        0,
                    ),
                    1,
                ),
            );
        },
    });

    // No link is no turn: the growth hook returns before it ever draws, which
    // would leave the invitation lit above a section that never arrives. The
    // product and project closings guard the same way.
    if (!next.link || !imageSrc) return null;

    return (
        // One item in the page's flex column, not two: the column's own gap
        // would otherwise open between the invitation and the section, on
        // top of the 800px the invitation already holds for. The margin
        // tops the column's gap up to the product page's 360 above it.
        <div className="w-full max-md:mt-16 md:mt-[11.25rem]">
            {/* Held at the middle of the screen before the turn, with the
                section riding up over it. Outside the section on purpose —
                the section is clipped to the next page's opening. */}
            <NextListingInvitation label="Next Article" fadeRef={labelRef} />
            {/* Exactly one viewport tall: the scroll clamp then stops with the
                banner's opening in place rather than past it, clipping it at
                the fold exactly as the next page's own fold will. */}
            <section
                ref={sectionRef}
                aria-label={`Next article: ${title}`}
                className={cn(
                    'relative z-10 h-screen w-full overflow-hidden',
                    ARTICLE_GROUND,
                )}
            >
                {/* A link, not only a scroll target. The growth is the
                invitation, but the next article has to be reachable by a
                click and by the keyboard whether or not the page ever turns
                itself. */}
                <RoccoLink
                    to={next.link}
                    // No link prefetch: Safari ignores the tag, so the scroll
                    // handler warms the route data with a plain fetch instead.
                    aria-label={`Go to ${title}`}
                    className="block no-underline"
                    onClick={() =>
                        log('NextArticleTurn', {
                            url: new URL(window.location.href),
                            data: { NextLink: next.link, Trigger: 'click' },
                        })
                    }
                >
                    {/* Laid out at the size it ends on and scaled down from
                    there, so growing costs one composited transform rather
                    than a re-layout of the banner. */}
                    <div
                        ref={frameRef}
                        style={{
                            transform: `scale(${NEXT_LISTING_START_SCALE})`,
                        }}
                        className="origin-top will-change-transform"
                    >
                        <div className="relative w-full">
                            {/* The masthead's own image attributes on the
                            masthead's own rendition, so the browser picks
                            the very candidate the next page will paint. */}
                            <img
                                {...getImageAttrs(imageSrc, {
                                    sizes: REVAMP_HERO_SIZES,
                                })}
                                alt={title}
                                width={1920}
                                height={1389}
                                decoding="async"
                                className={cn(HERO_BOX, 'object-cover')}
                            />
                            <div aria-hidden="true" className={TINT} />
                            <div aria-hidden="true" className={BOTTOM_SCRIM} />
                            {/* The next page's own headline, where its masthead
                            puts it, arriving as the banner does. */}
                            <div className={HEADLINE_PLACE}>
                                <div className={CONTENT_CAP_PADDED}>
                                    <p
                                        ref={titleRef}
                                        aria-hidden="true"
                                        style={{ opacity: 0 }}
                                        className={HEADLINE}
                                    >
                                        {title}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </RoccoLink>
            </section>
        </div>
    );
};
